import Toast from '~/components/toast.vue'
import { createApp, h } from 'vue'

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastFunction {
    (message: string, type?: ToastType, duration?: number): void
    success: (message: string, duration?: number) => void
    error: (message: string, duration?: number) => void
    info: (message: string, duration?: number) => void
    warning: (message: string, duration?: number) => void
}

export default defineNuxtPlugin((nuxtApp) => {
    // Create a container for the toasts if it doesn't exist
    const createContainer = (): HTMLElement => {
        const container = document.getElementById('toast-container')
        if (container) return container

        const newContainer = document.createElement('div')
        newContainer.id = 'toast-container'
        document.body.appendChild(newContainer)
        return newContainer
    }

    // Function to show a toast notification
    const toast: ToastFunction = (message: string, type: ToastType = 'info', duration = 3000) => {
        const container = createContainer()

        // Create a div for this specific toast
        const toastElement = document.createElement('div')
        container.appendChild(toastElement)

        // Create and mount the toast Vue component
        const toastApp = createApp({
            render() {
                return h(Toast, {
                    message,
                    type,
                    onClose: () => {
                        toastApp.unmount()
                        container.removeChild(toastElement)
                    }
                })
            }
        })

        toastApp.mount(toastElement)

        setTimeout(() => {
            const toastComponent = toastElement.querySelector('.toast-notification')
            if (toastComponent) {
                toastComponent.classList.add('toast-fade-out')
                setTimeout(() => {
                    toastApp.unmount()
                    container.removeChild(toastElement)
                }, 300)
            }
        }, duration)
    }

    // Add variants for different toast types
    toast.success = (message: string, duration?: number) => toast(message, 'success', duration)
    toast.error = (message: string, duration?: number) => toast(message, 'error', duration)
    toast.info = (message: string, duration?: number) => toast(message, 'info', duration)
    toast.warning = (message: string, duration?: number) => toast(message, 'warning', duration)

    return {
        provide: {
            toast
        }
    }
})